import {
  Autocomplete,
  Box,
  Button,
  Card,
  Chip,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  TextField,
  Checkbox,
  useTheme,
} from '@mui/material';
import { type IMeasure } from '../../../Interfaces/ILegalTransactionMeasure';

import { ErrorScreen } from '../../../core/ErrorScreen';
import { LoadingSpinner } from '../../../core/LoadingSpinner';
import { useState, type Dispatch, type SetStateAction } from 'react';
import type { ILegalTransactionType } from '../../../Interfaces/ILegalTransactionType';
import {
  useGetLegalTransactionTypeFullObject,
  useGetLegalTransactionTypes,
} from '../../../hooks/reactQuerys/LegalTransactionQueries';
import { useGetMeasurements } from '../../../hooks/reactQuerys/MeasurementQueries';

const ImportableMeasuresList = ({
  legalTransactionTypeId,
  selectedMeasures,
  setSelectedMeasures,
}: {
  legalTransactionTypeId: number;
  selectedMeasures: IMeasure[];
  setSelectedMeasures: Dispatch<SetStateAction<IMeasure[]>>;
}) => {
  const theme = useTheme();
  const {
    legalTransactionTypeFullObject,
    isLoadingLegalTransactionTypeFullObject,
  } = useGetLegalTransactionTypeFullObject(legalTransactionTypeId);

  const { measurements, isLoadingMeasurements } = useGetMeasurements();

  if (isLoadingLegalTransactionTypeFullObject) {
    return <LoadingSpinner height='50vh' />;
  }

  if (isLoadingMeasurements) {
    return <LoadingSpinner height='50vh' />;
  }

  if (!measurements) {
    return <ErrorScreen height='50vh' />;
  }

  if (!legalTransactionTypeFullObject) {
    return <ErrorScreen height='50vh' />;
  }

  if (
    !legalTransactionTypeFullObject.Measurements ||
    legalTransactionTypeFullObject.Measurements.length === 0
  ) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: theme.palette.grey[50],
          borderRadius: 1,
          border: `1px solid ${theme.palette.divider}`,
          minHeight: '50vh',
          p: 2,
        }}
      >
        <Typography variant='h6'>
          Keine Vorgangsmanager-Positionen gefunden
        </Typography>
        <Typography variant='body1'>
          Für diese Rechtsgeschäftsart sind keine Vorgangsmanager-Positionen
          definiert.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', p: 1 }}>
        <Checkbox
          checked={selectedMeasures.length === measurements.length}
          onChange={() =>
            setSelectedMeasures(prev =>
              prev.length === measurements.length ? [] : measurements
            )
          }
        />
        <Typography>Alle auswählen</Typography>
      </Box>

      {legalTransactionTypeFullObject.Measurements.map(measure => (
        <Card variant='outlined' key={measure.idMeasurement}>
          <CardContent
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 1,
              '&:last-child': {
                pb: 1,
              },
            }}
          >
            <Box display='flex' gap={2} alignItems='center'>
              <Checkbox
                checked={selectedMeasures.some(
                  m => m.idMeasurement === measure.idMeasurement
                )}
                onChange={() => {
                  setSelectedMeasures(prev =>
                    prev.some(m => m.idMeasurement === measure.idMeasurement)
                      ? prev.filter(
                          m => m.idMeasurement !== measure.idMeasurement
                        )
                      : [...prev, measure]
                  );
                }}
              />
              <Typography variant='subtitle1'>{measure.Name}</Typography>
              <Chip
                size='small'
                label={
                  measure.attributes.length === 0
                    ? 'Keine Attribute'
                    : `${measure.attributes.length} Attribute`
                }
              />
            </Box>
          </CardContent>
        </Card>
      ))}
    </Box>
  );
};

export const ImportMeasuresDialog = ({
  onClose,
  onImportMeasures,
}: {
  onClose: () => void;
  onImportMeasures: (measures: IMeasure[]) => void;
}) => {
  const { legalTransactionTypes, isLoadingLegalTransactionTypes } =
    useGetLegalTransactionTypes();

  const [selectedLegalTransactionType, setSelectedLegalTransactionType] =
    useState<ILegalTransactionType | null>(null);
  const [selectedMeasures, setSelectedMeasures] = useState<IMeasure[]>([]);

  const handleImportMeasures = () => {
    const measuresWithNewIds = selectedMeasures.map(measure => ({
      ...measure,
      id: crypto.randomUUID(),
      attributes: measure.attributes.map(attr => ({
        ...attr,
        id: crypto.randomUUID(),
      })),
    }));

    onImportMeasures(measuresWithNewIds);
    onClose();
  };

  if (isLoadingLegalTransactionTypes) {
    return (
      <Dialog open={true} onClose={onClose} fullWidth maxWidth='md'>
        <LoadingSpinner height='50vh' />
      </Dialog>
    );
  }

  if (!legalTransactionTypes) {
    return (
      <Dialog open={true} onClose={onClose} fullWidth maxWidth='md'>
        <ErrorScreen height='50vh' />
      </Dialog>
    );
  }

  return (
    <Dialog open={true} onClose={onClose} fullWidth maxWidth='md'>
      <DialogTitle>Aus Rechtsgeschäftsart importieren</DialogTitle>
      <DialogContent
        sx={{
          minHeight: '50vh',
        }}
      >
        <Autocomplete
          sx={{
            mt: 1,
          }}
          options={legalTransactionTypes}
          getOptionLabel={option => option.LegalTransactionType}
          getOptionKey={option => option.idLegalTransactionType}
          renderInput={params => (
            <TextField
              {...params}
              label='Rechtsgeschäft auswählen'
              variant='outlined'
              fullWidth
            />
          )}
          onChange={(_, value) => {
            if (value) {
              setSelectedLegalTransactionType(value);
            }
          }}
          fullWidth
        />
        {selectedLegalTransactionType && (
          <Box sx={{ mt: 2 }}>
            <ImportableMeasuresList
              legalTransactionTypeId={
                selectedLegalTransactionType.idLegalTransactionType
              }
              selectedMeasures={selectedMeasures}
              setSelectedMeasures={setSelectedMeasures}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant='outlined' onClick={onClose}>
          Abbrechen
        </Button>
        <Button variant='contained' onClick={handleImportMeasures}>
          Importieren
        </Button>
      </DialogActions>
    </Dialog>
  );
};
